import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Sparkles, Check, X, Loader2, Undo2 } from "lucide-react";

const SOURCE_WORDS = { build_sheet: "build sheet", photos: "photos" };

// C2 — THE AI'S SELLABLE LIST, GREY UNTIL A PERSON TAPS (his call, 30 Oct). "It has one" puts the component on this
// truck as PRESENT · NOT VERIFIED with his name (a new name goes on the waiting list under its own spelling); "No" is
// kept on record. Nothing the AI lists is counted, searched or shown as fact until then. Every tap: one tap back.
export const ComponentSuggestions = ({ donorId, items, answered, canAnswer, via = "desk", big = false, onChanged, testid = "comp-suggestions" }) => {
  const [busy, setBusy] = useState(null);
  if (!items?.length && !answered?.length) return null;
  const undo = async (code) => {
    setBusy(`${code}:undo`);
    try { const { data } = await api.delete(`/donors/${donorId}/component-suggestions/${code}/answer`); onChanged?.(data); toast.success("Put back"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't put it back"); }
    finally { setBusy(null); }
  };
  const answer = async (it, a) => {
    setBusy(`${it.component}:${a}`);
    try {
      const { data } = await api.post(`/donors/${donorId}/component-suggestions/${it.component}/answer`, { answer: a, via });
      onChanged?.(data);
      toast.success(a === "has" ? `${it.label}: on the truck, not verified` : `${it.label}: not on this truck`,
        { action: { label: "Undo", onClick: () => undo(it.component) } });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save that"); }
    finally { setBusy(null); }
  };
  const btn = big ? "h-12 px-4 text-base rounded-xl" : "h-8 px-3 text-xs rounded-lg";
  return (
    <div data-testid={testid} className="rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-3 space-y-2">
      {items?.length > 0 && (
        <p className="text-xs text-slate-500 flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-[#C76714]" />
          The AI thinks this truck has these to sell — nobody has confirmed them. {canAnswer ? "Tap each one." : "Someone who can rate components answers them."}
        </p>
      )}
      {(items || []).map((it) => (
        <div key={it.component} data-testid={`${testid}-row-${it.component}`} className="flex flex-wrap items-center gap-2 rounded-lg bg-white/70 px-3 py-2 text-slate-500">
          <span className={`font-semibold ${big ? "text-base" : "text-sm"}`}>{it.label}</span>
          {!it.on_list && <span data-testid={`${testid}-new-${it.component}`} className="rounded border border-amber-200 bg-amber-50 px-1.5 text-[10px] font-mono uppercase text-amber-800">new name</span>}
          {it.reason && <span className="text-xs">— {it.reason}</span>}
          <span className="font-mono text-[10px] uppercase text-slate-400">{it.sources.map((s) => SOURCE_WORDS[s] || s).join(" + ")}</span>
          <span className="flex-1" />
          {canAnswer && (
            <>
              <button type="button" data-testid={`${testid}-has-${it.component}`} disabled={!!busy} onClick={() => answer(it, "has")}
                className={`${btn} inline-flex items-center gap-1 border-2 border-emerald-600 bg-white font-bold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50`}>
                {busy === `${it.component}:has` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}It has one
              </button>
              <button type="button" data-testid={`${testid}-no-${it.component}`} disabled={!!busy} onClick={() => answer(it, "no")}
                className={`${btn} inline-flex items-center gap-1 border-2 border-slate-300 bg-white font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-50`}>
                {busy === `${it.component}:no` ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}No
              </button>
            </>
          )}
        </div>
      ))}
      {answered?.length > 0 && (
        <div data-testid={`${testid}-answered`} className="flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
          <span className="font-bold uppercase tracking-wider text-[10px] text-slate-400">Answered</span>
          {answered.map((a) => (
            <span key={a.component} data-testid={`${testid}-answered-${a.component}`} className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-0.5">
              {a.label}: <b>{a.answer === "has" ? "it has one" : "no"}</b> · {(a.by || "").split("@")[0]}
              {canAnswer && (
                <button type="button" data-testid={`${testid}-putback-${a.component}`} disabled={!!busy} onClick={() => undo(a.component)}
                  className={`inline-flex items-center gap-0.5 font-semibold text-[#B01E28] hover:underline disabled:opacity-50 ${big ? "min-h-12 px-2" : ""}`}>
                  <Undo2 className="w-3 h-3" />put back
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
